import search from '@/pages/myapp/reservation/search';
import { IReviews } from '@/types';
import { groupDatesByRate } from '@/utils/datesFn';
import { fetchTemplate, isServer } from '@/utils/isServer';
import dayjs, { Dayjs } from 'dayjs';

class ReservationService {
  totalLoaded = 5;
  public async GetReviews(facility_id, count, type_review) {
    const reviewsResponse = await fetch(
      `/api/FacilityReview/LoadData/${facility_id}`,
      {
        ...fetchTemplate,
        body: new URLSearchParams(
          `start=${count}&length=${this.totalLoaded}&extrasearch[type_review]=${type_review}`
        ),
        method: 'post',
      }
    );

    const reviews = await reviewsResponse.json();
    return reviews;
  }
  public async GetReviewsByStar(
    facility_id: number,
    count: number,
    type_review: number,
    star_review = 5
  ) {
    const reviewsResponse = await fetch(
      `/api/FacilityReview/LoadData/${facility_id}`,
      {
        ...fetchTemplate,
        body: new URLSearchParams(
          `start=${count}&length=${this.totalLoaded}&extrasearch[type_review]=${type_review}&extrasearch[star_review]=${star_review}`
        ),
        method: 'post',
      }
    );

    const reviews = await reviewsResponse.json();
    return reviews;
  }
  public async GetNearBy({
    facility_id,
    airport_id,
    facility_url_code,
    checkin,
    checkout,
  }: {
    facility_id?: number;
    airport_id?: number;
    facility_url_code?: string;
    checkin: Dayjs;
    checkout: Dayjs;
  }) {
    const response = await fetch(`/api/Facility/SearchNearBy`, {
      ...fetchTemplate,
      headers: {
        ...fetchTemplate.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        facility_id: facility_id,
        airport_id: airport_id,
        facility_url_code: facility_url_code,
        start_date: checkin.format('YYYY-MM-DDTHH:mm'),
        end_date: checkout.format('YYYY-MM-DDTHH:mm'),
      }),
      method: 'post',
    });

    const json = await response.json();
    return json;
  }

  public async GetReviewsBySortStar(
    facility_id: number,
    count: number,
    type_review: number
  ) {
    const reviewsResponse = await fetch(
      `/api/FacilityReview/LoadData/${facility_id}`,
      {
        ...fetchTemplate,
        body: new URLSearchParams(
          `start=${count}&length=${this.totalLoaded}&extrasearch[type_review]=${type_review}&extrasearch[star_order]=1`
        ),
        method: 'post',
      }
    );

    const reviews = await reviewsResponse.json();
    return reviews;
  }

  public async GetReviewsThirdParty(facility_url_code:string,
    count: number,
    type_review: number,
    star_review = 5 // Discard, using only order
  ): Promise<any> {
    const reviewsResponse = await fetch(
      `/api/FacilityReview/LoadDataThirdPartyNew/${facility_url_code}`,
      {
        ...fetchTemplate,
        body: new URLSearchParams(
          `start=${count}&length=${this.totalLoaded}&extrasearch[type_review]=${type_review}&extrasearch[star_order]=1`
        ),
        method: 'post',
      }
    );

    const reviews = await reviewsResponse.json();
    return reviews;
  }
  public async checkCoupon(
    coupon: string,
    email: string,
    facilityId: string,
    facilityUrlCode: string,
    checkin: string,
    checkout: string
  ) {
    const response = await fetch('/api/Coupon/Validate', {
      ...fetchTemplate,
      method: 'POST',
      body: `coupon_code=${coupon}&email=${
        email ? encodeURIComponent(email) : ''
      }&facility_id=${facilityId}&facility_url_code=${facilityUrlCode}&checkin=${checkin}&checkout=${checkout}`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    const data = await response.json();
    return data;
  }

  public async GetPrice(
    data: any,
    member_id: number,
    checkin: string,
    checkout: string
  ) {
    const abtest = !isServer ? sessionStorage.getItem('abtest') : "";
    const insurance_id = !isServer ? sessionStorage.getItem('insurance_id') : "";
    const checkout_mode = !isServer ? sessionStorage.getItem('checkout_mode') : "";
    const priceResponse = await fetch(`${data.baseUrl ?? ''}/api/Reservation/CalculatePrice`, {
      ...fetchTemplate,
      body: new URLSearchParams(
        `facility_id=${data.facility_id}&facility_url_code=${
          data.facility_url_code
        }&reservation_dt_checkin=${dayjs(checkin).format(
          'MM.DD.YYYY h:mm a'
        )}&reservation_dt_checkout=${dayjs(checkout).format(
          'MM.DD.YYYY h:mm a'
        )}&member_id=${member_id}&coupon_code=${
          data.coupon_code ?? ''
        }&coupon_discount=${data.coupon_discount ?? ''}&reservation_fee=${
          data.reservation_fee ?? ''
        }&reservation_tax=${data.reservation_tax ?? ''}&stripeToken=${
          data.stripeToken ?? ''
        }&reservation_first_name=${
          data.reservation_first_name
            ? encodeURIComponent(data.reservation_first_name)
            : ''
        }&reservation_last_name=${
          data.reservation_last_name
            ? encodeURIComponent(data.reservation_last_name)
            : ''
        }&reservation_email=${
          data.reservation_email
            ? encodeURIComponent(data.reservation_email)
            : ''
        }&reservation_phone=${
          data.reservation_phone
            ? encodeURIComponent(data.reservation_phone)
            : ''
        }&zipcode=${data.zipcode ?? ''}&card_name=${
          data.card_name ?? ''
        }&checkout_mode=${checkout_mode}&abtest=${abtest}&insurance_id=${insurance_id}`
      ),
      method: 'post',
    });
    const price = await priceResponse.json();
    const hasDueNow = price.dueNow !== price.totalPrice ? true : false;
    const hasDiscount = price.fullNosonFee !== price.discountedNosonFee || price.customerCouponDiscount;
    const priceDescription = Object.assign({
      hasDueNow,
      hasDiscount,
      ...price,
    });
    return priceDescription;
  }

  public async GetDailyRates(facility_id, days_parked, checkin, checkout) {
    const checkout_mode = sessionStorage.getItem('checkout_mode');
    const dailyRatesResponse = await fetch(
      `/api/facility/dates?facility_id=${facility_id}&start_date=${checkin}&end_date=${checkout}&checkout_mode=${checkout_mode}`,
      {
        ...fetchTemplate,
        method: 'post',
      }
    );

    const dailyRates = await dailyRatesResponse.json();
    const daysParked = days_parked || dailyRates.dates.length - 1;
    const datePricesMinusOne = dailyRates.dates.slice(0, daysParked);
    const groupedDates = groupDatesByRate(datePricesMinusOne);
    return groupedDates;
  }

  public getGroupedDatesReservation(
    startDate: Dayjs,
    endDate: Dayjs,
    days_parked: number,
    selling: any
  ): any {
    const dailyRates = [];
    let fail = false;
    for (let it = startDate; it.isBefore(endDate); it = it.add(1, 'day')) {
      const keyDate = it.format('YYYY-MM-DD');
      const rate = selling[keyDate];
      if (!rate) {
        fail = true;
        break;
      }
      dailyRates.push({ date: keyDate + 'T00:00:00', rate });
    }
    if (fail) return null;

    const daysParked = days_parked || dailyRates.length - 1;
    const datePricesMinusOne = dailyRates.slice(0, daysParked);
    const groupedDates = groupDatesByRate(datePricesMinusOne);

    return groupedDates;
  }
}

export default new ReservationService();

